import { useCallback, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

export const useExperienceFilters = (initialTab = "published") => {
  const [activeTab, setActiveTab] = useState("all")
  const [activeListingTab, setActiveListingTab] = useState(initialTab)
  const [selectedType, setSelectedType] = useState("private")
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const categoriesParam = searchParams.get("categories") || ""
  const durationTypesParam = searchParams.get("duration_types") || ""

  const selectedCategoryIds = useMemo(
    () => (categoriesParam ? categoriesParam.split(",").map(Number).filter(Boolean) : []),
    [categoriesParam]
  )
  const selectedDurationTypes = useMemo(
    () => (durationTypesParam ? durationTypesParam.split(",").filter(Boolean) : []),
    [durationTypesParam]
  )
  const activeFilterCount = selectedCategoryIds.length + selectedDurationTypes.length

  const buildFilterQuery = useCallback(() => {
    const q = {}
    if (selectedCategoryIds.length > 0) q.category_ids_in = selectedCategoryIds
    if (selectedDurationTypes.length > 0) q.duration_type_in = selectedDurationTypes
    return q
  }, [selectedCategoryIds, selectedDurationTypes])

  const applyFilters = useCallback(
    ({ categoryIds = [], durationTypes = [] }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (categoryIds.length > 0) next.set("categories", categoryIds.join(","))
          else next.delete("categories")
          if (durationTypes.length > 0) next.set("duration_types", durationTypes.join(","))
          else next.delete("duration_types")
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const clearFilters = useCallback(() => applyFilters({ categoryIds: [], durationTypes: [] }), [applyFilters])

  const removeCategoryFilter = useCallback(
    (id) => applyFilters({ categoryIds: selectedCategoryIds.filter((v) => v !== id), durationTypes: selectedDurationTypes }),
    [applyFilters, selectedCategoryIds, selectedDurationTypes]
  )

  const removeDurationTypeFilter = useCallback(
    (value) => applyFilters({ categoryIds: selectedCategoryIds, durationTypes: selectedDurationTypes.filter((v) => v !== value) }),
    [applyFilters, selectedCategoryIds, selectedDurationTypes]
  )

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab)
  }, [])

  const handleListingTabChange = useCallback((tab) => {
    setActiveListingTab(tab)
    setSelectedType("")
  }, [])

  const handleFilterIconClick = useCallback(() => {
    setIsFilterDropdownOpen((prev) => !prev)
  }, [])

  const handleCloseFilterDropdown = useCallback(() => {
    setIsFilterDropdownOpen(false)
  }, [])

  return {
    activeTab,
    activeListingTab,
    selectedType,
    isFilterDropdownOpen,
    handleTabChange,
    handleListingTabChange,
    setSelectedType,
    handleFilterIconClick,
    handleCloseFilterDropdown,
    selectedCategoryIds,
    selectedDurationTypes,
    activeFilterCount,
    buildFilterQuery,
    applyFilters,
    clearFilters,
    removeCategoryFilter,
    removeDurationTypeFilter
  }
}
